import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, DatePicker, Modal, Select, Space, Table, TableColumnsType, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";

import StringUtils from "../../../../../../util/StringUtils";
import FileUtils from "../../../../../../util/FileUtils";
import ByteUtils from "../../../../../../util/ByteUtils";
import SessionService from "../../../../../../services/session/SessionService";
import Api from "../../../../../../api/Api";
import WaveformFileApi from "../../../../../../api/WaveformFileApi";
import WaveformFileViewer from "./waveform-viewer/WaveformFileViewer";
import { FilterBar, FilterItem, FilterItemLabel, WaveformFileManagementWrapper, TableWrapper } from "./WaveformFile.styled";

interface WaveformFileProps {
    deviceView: any
}

let WaveformFile: React.FC<WaveformFileProps> = (props)=> {
    // params
    let deviceView = props.deviceView;

    // api
    let waveformFileApi = new WaveformFileApi();
    let navigate = useNavigate();
    let [messageApi, contextHolder] = message.useMessage();

    // signal types
    let dataTypeOptions = [
        { value: 'EXC', label: '异常波形' },
    ];

    // state
    let [loading, setLoading] = useState(true);
    let [dataType, setDataType] = useState(dataTypeOptions[0].value);
    let [waveformFileViews, setWaveformFileViews] = useState([]);
    let [openWaveformFileViewer, setOpenWaveformFileViewer] = useState(false);
    let [targetWaveformFileView, setTargetWaveformFileView] = useState<any>(null);

    let [startTime, setStartTime] = useState<dayjs.Dayjs>();
    let [endTime, setEndTime] = useState<dayjs.Dayjs>();

    // functions
    let loadWaveformFileViews = async ()=> {
        setLoading(true);
        let waveformFileViews = (await waveformFileApi.queryDeviceWaveformFileViews({
            deviceId: deviceView.device.id,
            type: dataType,
            startTime: startTime?.toDate().getTime(),
            endTime: endTime?.toDate().getTime(),
        })).data.data;
        for(let waveformFileView of waveformFileViews) {
            waveformFileView.children=waveformFileView.waveformFileViews;
        }
        setWaveformFileViews(waveformFileViews);
        setLoading(false);
    };

    let formatFileName = (fileName: string)=> {
        if (fileName.lastIndexOf('/') >= 0) {
            return fileName.substring(fileName.lastIndexOf('/') + 1);
        }
        return fileName;
    }

    let formatDataType = (dataType)=> {
        for(let dataTypeOption of dataTypeOptions) {
            if (dataTypeOption.value == dataType) {
                return dataTypeOption.label;
            }
        }
        return dataType;
    };

    let formatTimestampUs = (timestampUs)=> {
        if (timestampUs == null) {
            return '';
        }
        return moment(parseInt(timestampUs)/1000).format('YYYY-MM-DD HH:mm:ss');
    };

    let getDownloadDataFileUrl = (dataFileView)=> {
        // let downloadUrl = Api.URL_PREFIX + `/pzt/data-file/${dataFileView.dataFile.dataType.toLowerCase()}/${moment(parseInt(dataFileView.detaFile.created)).format('YYYY-MM-DD')}/${dataFileView.name}?&authorization=${SessionService.getInstance().getAuthoriazation()}`;
        let downloadUrl = Api.URL_PREFIX + `/waveform-file/download?id=${dataFileView.waveformFile.id}&authorization=${SessionService.getInstance().getAuthoriazation()}`;
        return downloadUrl;
    };

    let onViewWaveformFile = (dataFileView)=> {
        setOpenWaveformFileViewer(true);
        setTargetWaveformFileView(dataFileView);
    }

    let onExportExcel = async (dataFileView)=> {
        let excelFileBlobResp = await waveformFileApi.exportToExcel(dataFileView.waveformFile.id);
        let url = window.URL.createObjectURL(new Blob([excelFileBlobResp.data]));
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url;
        a.download = FileUtils.getFileNameFromPath(dataFileView.waveformFile.name) + '.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    }

    let onDateTimeRangeChange = (dateTimeRangeValue: any[])=> {
        setStartTime(dateTimeRangeValue[0]);
        setEndTime(dateTimeRangeValue[1]);
    };

    // effect
    useEffect(()=> {
        loadWaveformFileViews();
    }, [dataType, startTime, endTime]);

    // render
    const dataFileColumns: TableColumnsType<any> = [
        {
            title: '日期/文件名',
            dataIndex: 'name',
            key: 'name',
            width: '200px',
            render: (text, record, index)=> {
                return <div style={{whiteSpace: 'nowrap', textAlign: 'left'}}>{formatFileName(record.waveformFile.name)}</div>
            },
        },
        {
            title: '数据类型',
            dataIndex: 'dataType',
            key: 'dataType',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{formatDataType(record.waveformFile.type)}</span>
            }
        },
        {
            title: '起始时间',
            dataIndex: 'startTime',
            key: 'startTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{formatTimestampUs(record.waveformFile.startTimestampUs)}</span>
            }
        },
        {
            title: '结束时间',
            dataIndex: 'endTime',
            key: 'endTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{formatTimestampUs(record.waveformFile.endTimestampUs)}</span>
            }
        },
        {
            title: '文件大小',
            dataIndex: 'size',
            key: 'size',
            align: 'center',
            render: (text, record, index)=> {
                let size = Number(record.waveformFile.size);
                if (size == null) {
                    return StringUtils.emptyToSlash('');
                }
                return ByteUtils.formatBytes(size);
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return record.waveformFile.id && <Space size="middle">
                    {record.waveformFile.type && <a href={getDownloadDataFileUrl(record)}>下载</a>}
                    {record.waveformFile.type && <a onClick={()=> onViewWaveformFile(record)}>查看</a>}
                    {record.waveformFile.type && <a onClick={()=> onExportExcel(record)}>导出</a>}
                </Space>
            }
            
        }
    ];

    return <WaveformFileManagementWrapper>
            {contextHolder}
            <FilterBar>
                <Space size="middle">
                    <FilterItem>
                        <FilterItemLabel>类型</FilterItemLabel>
                        <Select
                            maxTagCount={2}
                            style={{ width: '150px' }}
                            placeholder="请选择数据类型"
                            value={dataType}
                            onChange={(selectedValue)=> setDataType(selectedValue)}
                            options={dataTypeOptions}
                        />
                    </FilterItem>
                    
                    <FilterItem>
                        <FilterItemLabel>时间范围</FilterItemLabel>
                        <DatePicker.RangePicker
                            style={{width: '330px'}}
                            showTime={{ format: 'HH:mm:ss' }}
                            format="YYYY-MM-DD HH:mm:ss"
                            defaultValue={[dayjs().add(-1, 'd'), dayjs()]}
                            value={[startTime, endTime]}
                            allowEmpty={[false, true]}
                            onOk={onDateTimeRangeChange}
                        />
                    </FilterItem>
                </Space>

                <Space size="middle">
                    <FilterItem>
                        <Button type="primary" onClick={()=> loadWaveformFileViews()} icon={<ReloadOutlined />}>刷新</Button>
                    </FilterItem>
                </Space>
            </FilterBar>
            <TableWrapper>
                <Table 
                    style={{height: '100%'}}
                    rowKey={(record)=> record.waveformFile.name}
                    columns={dataFileColumns} 
                    dataSource={waveformFileViews}
                    loading={loading}
                    pagination={false}
                    scroll={{y: 'calc(100vh - 350px)'}}
                />
            </TableWrapper>
            
            <Modal open={openWaveformFileViewer} 
                onClose={()=> setOpenWaveformFileViewer(false)} 
                footer={null} 
                closable={false}
                destroyOnClose={true}
                width={1300}
                >
                <WaveformFileViewer waveformFileView={targetWaveformFileView} onClose={()=> setOpenWaveformFileViewer(false)} />
            </Modal>
    </WaveformFileManagementWrapper>
};

export default WaveformFile;
